import { useLang } from "../../context/LanguageContext";
import { useForm } from "../../context/FormContext";

type QualifyingGround = "work" | "family" | "vulnerability";

const QUALIFYING_CARDS: Array<{ ground: QualifyingGround; titleKey: string; descKey: string }> = [
  {
    ground: "work",
    titleKey: "section2e.qualifyingWorkTitle",
    descKey: "section2e.qualifyingWorkDescription",
  },
  {
    ground: "family",
    titleKey: "section2e.qualifyingFamilyTitle",
    descKey: "section2e.qualifyingFamilyDescription",
  },
  {
    ground: "vulnerability",
    titleKey: "section2e.qualifyingVulnerabilityTitle",
    descKey: "section2e.qualifyingVulnerabilityDescription",
  },
];

interface EligibilityRoutingSectionProps {
  mode: "asylum" | "qualifying";
}

/**
 * Step 2 split into two screens:
 * - `asylum`: asks whether applicant has filed asylum in Spain
 * - `qualifying`: asks non-asylum route (work/family/vulnerability)
 */
export default function EligibilityRoutingSection({ mode }: EligibilityRoutingSectionProps) {
  const { t } = useLang();
  const { data, update } = useForm();

  const funnelDefault =
    data.asylumApplicant === "yes" ? true : data.asylumApplicant === "no" ? false : null;

  const selectedAsylum = data.asylumRoutingConfirmed;
  const selectedGround = data.qualifyingGround as QualifyingGround | "";
  const resetDownstreamSituationBranch = () => {
    update("asylumScenario", "");
    update("oarCaseNumber", "");
    update("subRoute", "");
    update("irregularSubRoute", "");
    update("workHistory", "");
    update("prorrogaType", "");
    update("workType", "");
    update("selfEmploymentActivity", "");
    update("hasCriminalRecord", "");
    update("previousApplication", "");
  };

  const handleAsylumChange = (value: boolean) => {
    update("asylumRoutingConfirmed", value);
    update("asylumApplicant", value ? "yes" : "no");
    if (value === true) {
      update("qualifyingGround", "");
      resetDownstreamSituationBranch();
    } else {
      update("qualifyingGround", "");
      resetDownstreamSituationBranch();
    }
  };

  const handleQualifyingSelect = (ground: QualifyingGround) => {
    update("qualifyingGround", ground);
    update("asylumRoutingConfirmed", false);
    update("asylumApplicant", "no");
  };

  return (
    <div className="max-w-[680px] mx-auto px-4 pt-6 pb-2">
      {mode === "asylum" ? (
        <section>
        <div className="mb-7">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-5 h-5 rounded-md bg-emerald-600 shrink-0" />
            <h1 className="text-xl font-display text-slate-900 leading-tight">
              {t("section2e.asylumRoutingTitle")}
            </h1>
          </div>
          <p className="text-[14px] text-slate-500 leading-relaxed">{t("section2e.asylumRoutingSubtitle")}</p>
        </div>

        <div
          className="flex flex-col min-[480px]:flex-row gap-3"
          data-field="asylumRoutingConfirmed"
          id="field-asylumRoutingConfirmed"
        >
          {[
            {
              value: true,
              title: t("section2e.asylumRoutingYesTitle"),
              description: t("section2e.asylumRoutingYesDescription"),
            },
            {
              value: false,
              title: t("section2e.asylumRoutingNoTitle"),
              description: t("section2e.asylumRoutingNoDescription"),
            },
          ].map((option) => {
            const isSelected = selectedAsylum === option.value;
            const isPreselected = selectedAsylum === null && funnelDefault === option.value;
            return (
              <button
                key={String(option.value)}
                type="button"
                onClick={() => handleAsylumChange(option.value)}
                className={[
                  "flex-1 min-h-[88px] text-left px-4 py-5 rounded-xl border-2 transition-all duration-200 cursor-pointer",
                  isSelected
                    ? "bg-emerald-50 border-emerald-400"
                    : isPreselected
                      ? "bg-emerald-50/50 border-emerald-200"
                      : "bg-white border-slate-200 hover:border-emerald-200 hover:bg-emerald-50/30",
                ].join(" ")}
              >
                <p
                  className={[
                    "text-[14px] font-semibold leading-snug mb-1.5",
                    isSelected ? "text-emerald-800" : isPreselected ? "text-emerald-700" : "text-slate-800",
                  ].join(" ")}
                >
                  {option.title}
                </p>
                <p
                  className={[
                    "text-[13px] leading-relaxed",
                    isSelected ? "text-emerald-700" : isPreselected ? "text-emerald-600" : "text-slate-500",
                  ].join(" ")}
                >
                  {option.description}
                </p>
              </button>
            );
          })}
        </div>
      </section>
      ) : (
      <section>
        <div className="mb-7">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-5 h-5 rounded-md bg-emerald-600 shrink-0" />
            <h2 className="text-xl font-display text-slate-900 leading-tight">{t("section2e.qualifyingTitle")}</h2>
          </div>
          <p className="text-[14px] text-slate-500 leading-relaxed">{t("section2e.qualifyingSubtitle")}</p>
        </div>

        <div
          className="flex flex-col min-[480px]:flex-row gap-3"
          data-field="qualifyingGround"
          id="field-qualifyingGround"
        >
          {QUALIFYING_CARDS.map(({ ground, titleKey, descKey }) => {
            const isSelected = selectedGround === ground;
            return (
              <button
                key={ground}
                type="button"
                onClick={() => handleQualifyingSelect(ground)}
                className={[
                  "flex-1 min-h-[88px] text-left px-4 py-5 rounded-xl border-2 transition-all duration-200 cursor-pointer",
                  isSelected
                    ? "bg-emerald-50 border-emerald-400"
                    : "bg-white border-slate-200 hover:border-emerald-200 hover:bg-emerald-50/30",
                ].join(" ")}
              >
                <p
                  className={[
                    "text-[14px] font-semibold leading-snug mb-1.5 min-h-[52px]",
                    isSelected ? "text-emerald-800" : "text-slate-800",
                  ].join(" ")}
                >
                  {t(titleKey)}
                </p>
                <p
                  className={[
                    "text-[13px] leading-relaxed",
                    isSelected ? "text-emerald-700" : "text-slate-500",
                  ].join(" ")}
                >
                  {t(descKey)}
                </p>
              </button>
            );
          })}
        </div>
      </section>
      )}
    </div>
  );
}
